import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

/**
 * Per-job bytes-transferred progress for in-flight ingest uploads.
 *
 * Populated by the XHR-based upload mutations in `ontology-api.ts` from
 * `xhr.upload.onprogress` events; consumed by the Jobs table row to render
 * a live progress bar while the job sits in state="uploading".
 *
 * Kept client-only — the server cannot know how many bytes a given browser
 * has sent in real time. The slice is cleared per-job on completion /
 * failure so the cache doesn't accumulate finished uploads forever.
 */
export interface IngestUploadProgressEntry {
  uploadedBytes: number
  totalBytes: number
}

export interface IngestUploadProgressState {
  byJobId: Record<string, IngestUploadProgressEntry>
}

const initialState: IngestUploadProgressState = {
  byJobId: {}
}

const ingestUploadProgressSlice = createSlice({
  name: 'ingestUploadProgress',
  initialState,
  reducers: {
    setUploadProgress(
      state,
      action: PayloadAction<{ jobId: string; uploadedBytes: number; totalBytes: number }>
    ) {
      const { jobId, uploadedBytes, totalBytes } = action.payload
      state.byJobId[jobId] = { uploadedBytes, totalBytes }
    },
    clearUploadProgress(state, action: PayloadAction<{ jobId: string }>) {
      delete state.byJobId[action.payload.jobId]
    }
  }
})

export const { setUploadProgress, clearUploadProgress } = ingestUploadProgressSlice.actions

export default ingestUploadProgressSlice.reducer

interface RootStateLike {
  ingestUploadProgress: IngestUploadProgressState
}

export function selectUploadProgress(
  state: RootStateLike,
  jobId: string | undefined
): { pct: number; uploadedBytes: number; totalBytes: number } | undefined {
  if (!jobId) return undefined
  const entry = state.ingestUploadProgress.byJobId[jobId]
  if (!entry || entry.totalBytes <= 0) return undefined
  const pct = Math.min(100, Math.max(0, Math.round((entry.uploadedBytes / entry.totalBytes) * 100)))
  return { pct, uploadedBytes: entry.uploadedBytes, totalBytes: entry.totalBytes }
}
